import { useQuery } from '@apollo/client'
import { Add, Clear, ExpandMore } from '@mui/icons-material'
import {
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    Collapse,
    Divider,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Slider,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { DateRangeInput } from 'recruiter/components/DateRangeInput'
import {
    type GetProcessStatusesQuery,
    type GetRostersByIdsQuery,
    ProcessStatusGroup,
} from '__generated__/graphql'
import { GetCompaniesById } from 'candidate/graphql/generatedProfile.gql'
import startCase from 'lodash.startcase'
import { type FC, useState } from 'react'
import {
    SettingsSentence,
    type SentencePart,
} from 'recruiter/components/SettingsSentence'
import { GetCandidateNoteTemplates } from 'recruiter/graphql/candidateNotes.gql'
import CompaniesAutocompleteMultiple from 'shared/components/CompaniesAutocomplete/CompaniesAutocompleteMultiple'
import RostersAutocomplete from 'shared/components/RostersAutocomplete/RostersAutocomplete'
import { GetRostersByIds } from 'shared/components/RostersAutocomplete/RostersAutocomplete.gql'
import { GetProcessStatuses } from 'shared/graphql/candidateProcessStatus.gql'
import {
    type CandidateFilterState,
    type SearchFilterItem,
} from 'shared/hooks/candidateSearchUrlState'
import { useReferenceSearchesUrlState } from 'shared/hooks/useReferenceSearchesUrlState'
import {
    filterCheckboxSx,
    filterCheckboxLabelSx,
    filterLabelSx,
} from 'recruiter/filterStyles'
import {
    FilterWrapper,
    FilterAutocomplete,
    renderMinimalTags,
} from './FilterPrimitives'
import {
    minimalInputSx,
    minimalTagSx,
    toggleChipSx,
    compactListboxProps,
    smallCheckboxLabelSx,
} from './minimalFilterStyles'

// ─── Types ──────────────────────────────────────────────────────────────────

type FilterProps<K extends keyof CandidateFilterState> = {
    value: CandidateFilterState[K]
    onChange: (value: CandidateFilterState[K]) => void
}

// ─── Constants ──────────────────────────────────────────────────────────────

const EDUCATION_TYPE_OPTIONS = ['Undergraduate', 'Graduate']

// ─── Helpers ────────────────────────────────────────────────────────────────

const parseYearMonth = (ym: string): Date => {
    const [year, month] = ym.split('-').map(Number)
    return new Date(year, month - 1, 1)
}

const formatYearMonth = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

const formatDisplayDate = (ym: string): string => {
    const date = parseYearMonth(ym)
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

const getTimeFrameLabel = (
    timeFrame: string | undefined,
    rangeGte: string | undefined,
    rangeLte: string | undefined
): { spacer: string; label: string } => {
    const modifier =
        timeFrame === 'current'
            ? ', current'
            : timeFrame === 'former'
              ? ', past'
              : ''

    if (rangeGte && rangeLte) {
        if (rangeGte === rangeLte) {
            return {
                spacer: 'in ',
                label: `${formatDisplayDate(rangeGte)}${modifier}`,
            }
        }
        return {
            spacer: 'from ',
            label: `${formatDisplayDate(rangeGte)} to ${formatDisplayDate(rangeLte)}${modifier}`,
        }
    }
    if (rangeGte)
        return {
            spacer: 'from ',
            label: `${formatDisplayDate(rangeGte)} onwards${modifier}`,
        }
    if (rangeLte)
        return {
            spacer: 'until ',
            label: `${formatDisplayDate(rangeLte)}${modifier}`,
        }
    if (timeFrame === 'current') return { spacer: 'at ', label: 'this moment' }
    if (timeFrame === 'former')
        return { spacer: 'at ', label: 'any point in the past' }
    return { spacer: 'at ', label: 'any point' }
}

type AdvancedDateMode = 'none' | 'onOrAfter' | 'onOrBefore' | 'between'

const getAdvancedDateMode = (
    gte: string | undefined,
    lte: string | undefined
): AdvancedDateMode => {
    if (gte && lte) return 'between'
    if (gte) return 'onOrAfter'
    if (lte) return 'onOrBefore'
    return 'none'
}

interface AdvancedDateRowProps {
    label: string
    gte: string | undefined
    lte: string | undefined
    onChange: (gte: string | undefined, lte: string | undefined) => void
}

const AdvancedDateRow: FC<AdvancedDateRowProps> = ({
    label,
    gte,
    lte,
    onChange,
}) => {
    const mode = getAdvancedDateMode(gte, lte)
    const hasRangeError = mode === 'between' && !!gte && !!lte && gte > lte

    const handleModeChange = (newMode: AdvancedDateMode) => {
        if (newMode === 'none') {
            onChange(undefined, undefined)
        } else if (newMode === 'onOrAfter') {
            onChange(gte || formatYearMonth(new Date()), undefined)
        } else if (newMode === 'onOrBefore') {
            onChange(undefined, lte || formatYearMonth(new Date()))
        } else if (newMode === 'between') {
            onChange(
                gte || formatYearMonth(new Date()),
                lte || formatYearMonth(new Date())
            )
        }
    }

    const smallPickerSlotProps = {
        textField: {
            size: 'small' as const,
            error: hasRangeError,
            sx: {
                width: 80,
                '& .MuiOutlinedInput-root': {
                    fontSize: '0.6875rem',
                },
                '& .MuiOutlinedInput-input': {
                    py: 0.75,
                    pl: 1,
                    pr: 0,
                },
            },
        },
        openPickerButton: { sx: { p: 0.25 } },
        openPickerIcon: { sx: { fontSize: '0.875rem' } },
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                minHeight: 32,
            }}
        >
            <Typography
                sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    minWidth: 55,
                }}
            >
                {label}
            </Typography>
            <Select
                size="small"
                value={mode}
                onChange={(e) =>
                    handleModeChange(e.target.value as AdvancedDateMode)
                }
                sx={{
                    fontSize: '0.6875rem',
                    width: 110,
                    '& .MuiSelect-select': { py: 0.75, px: 1 },
                }}
            >
                <MenuItem value="none" sx={{ fontSize: '0.75rem' }}>
                    —
                </MenuItem>
                <MenuItem value="onOrAfter" sx={{ fontSize: '0.75rem' }}>
                    On or after
                </MenuItem>
                <MenuItem value="onOrBefore" sx={{ fontSize: '0.75rem' }}>
                    On or before
                </MenuItem>
                <MenuItem value="between" sx={{ fontSize: '0.75rem' }}>
                    Between
                </MenuItem>
            </Select>
            {mode === 'onOrAfter' && (
                <DatePicker
                    views={['year', 'month']}
                    format="MM/yyyy"
                    value={gte ? parseYearMonth(gte) : null}
                    onChange={(date) =>
                        onChange(
                            date ? formatYearMonth(date) : undefined,
                            undefined
                        )
                    }
                    maxDate={new Date()}
                    slotProps={smallPickerSlotProps}
                />
            )}
            {mode === 'onOrBefore' && (
                <DatePicker
                    views={['year', 'month']}
                    format="MM/yyyy"
                    value={lte ? parseYearMonth(lte) : null}
                    onChange={(date) =>
                        onChange(
                            undefined,
                            date ? formatYearMonth(date) : undefined
                        )
                    }
                    maxDate={new Date()}
                    slotProps={smallPickerSlotProps}
                />
            )}
            {mode === 'between' && (
                <>
                    <DatePicker
                        views={['year', 'month']}
                        format="MM/yyyy"
                        value={gte ? parseYearMonth(gte) : null}
                        onChange={(date) =>
                            onChange(
                                date ? formatYearMonth(date) : undefined,
                                lte
                            )
                        }
                        maxDate={new Date()}
                        slotProps={smallPickerSlotProps}
                    />
                    <Typography
                        sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}
                    >
                        and
                    </Typography>
                    <DatePicker
                        views={['year', 'month']}
                        format="MM/yyyy"
                        value={lte ? parseYearMonth(lte) : null}
                        onChange={(date) =>
                            onChange(
                                gte,
                                date ? formatYearMonth(date) : undefined
                            )
                        }
                        maxDate={new Date()}
                        slotProps={smallPickerSlotProps}
                    />
                    {/* {hasRangeError && (
                        <Typography
                            variant="body2"
                            color="error"
                            sx={{ fontSize: '0.5625rem' }}
                        >
                            Invalid
                        </Typography>
                    )} */}
                </>
            )}
        </Box>
    )
}

interface TimeFrameControlsProps {
    timeFrame: string | undefined
    rangeGte: string | undefined
    rangeLte: string | undefined
    onChangeTimeFrame: (tf: string | undefined) => void
    onChangeRange?: (gte: string | undefined, lte: string | undefined) => void
    advancedStartGte?: string
    advancedStartLte?: string
    advancedEndGte?: string
    advancedEndLte?: string
    onChangeAdvancedStart?: (
        gte: string | undefined,
        lte: string | undefined
    ) => void
    onChangeAdvancedEnd?: (
        gte: string | undefined,
        lte: string | undefined
    ) => void
}

export const TimeFrameControls: FC<TimeFrameControlsProps> = ({
    timeFrame,
    rangeGte,
    rangeLte,
    onChangeTimeFrame,
    onChangeRange,
    advancedStartGte,
    advancedStartLte,
    advancedEndGte,
    advancedEndLte,
    onChangeAdvancedStart,
    onChangeAdvancedEnd,
}) => {
    const [showAdvanced, setShowAdvanced] = useState(
        !!(
            advancedStartGte ||
            advancedStartLte ||
            advancedEndGte ||
            advancedEndLte ||
            timeFrame
        )
    )

    return (
        <Stack gap={1}>
            <DateRangeInput
                from={rangeGte ? parseYearMonth(rangeGte) : null}
                to={rangeLte ? parseYearMonth(rangeLte) : null}
                onFromChange={(date) =>
                    onChangeRange?.(
                        date ? formatYearMonth(date) : undefined,
                        rangeLte
                    )
                }
                onToChange={(date) =>
                    onChangeRange?.(
                        rangeGte,
                        date ? formatYearMonth(date) : undefined
                    )
                }
                fromLabel="Start"
                toLabel="End"
                maxDate={new Date()}
            />
            {onChangeAdvancedStart && onChangeAdvancedEnd && (
                <>
                    <Box
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            color: 'primary.main',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        <ExpandMore
                            sx={{
                                fontSize: '1rem',
                                transform: showAdvanced
                                    ? 'rotate(180deg)'
                                    : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                            }}
                        />
                        Advanced
                    </Box>
                    <Collapse in={showAdvanced}>
                        <Stack gap={0}>
                            <AdvancedDateRow
                                label="Start Date"
                                gte={advancedStartGte}
                                lte={advancedStartLte}
                                onChange={onChangeAdvancedStart}
                            />
                            <AdvancedDateRow
                                label="End Date"
                                gte={advancedEndGte}
                                lte={advancedEndLte}
                                onChange={onChangeAdvancedEnd}
                            />
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                                sx={{ minHeight: 32 }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '0.6875rem',
                                        fontWeight: 500,
                                        color: 'text.secondary',
                                        minWidth: 55,
                                    }}
                                >
                                    Status
                                </Typography>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={0.5}
                                    onClick={() =>
                                        onChangeTimeFrame(
                                            timeFrame === 'current'
                                                ? undefined
                                                : 'current'
                                        )
                                    }
                                    sx={{ cursor: 'pointer', mr: 1 }}
                                >
                                    <Checkbox
                                        size="small"
                                        disableRipple
                                        checked={timeFrame === 'current'}
                                        sx={{
                                            p: 0,
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '1rem',
                                            },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '0.6875rem',
                                            color: 'text.secondary',
                                            userSelect: 'none',
                                        }}
                                    >
                                        Current
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={0.5}
                                    onClick={() =>
                                        onChangeTimeFrame(
                                            timeFrame === 'former'
                                                ? undefined
                                                : 'former'
                                        )
                                    }
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <Checkbox
                                        size="small"
                                        disableRipple
                                        checked={timeFrame === 'former'}
                                        sx={{
                                            p: 0,
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '1rem',
                                            },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '0.6875rem',
                                            color: 'text.secondary',
                                            userSelect: 'none',
                                        }}
                                    >
                                        Past
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Collapse>
                </>
            )}
        </Stack>
    )
}

const formatListWithOr = (items: string[]): string => {
    if (items.length === 0) return ''
    if (items.length === 1) return items[0]
    if (items.length === 2) return `${items[0]} or ${items[1]}`
    return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`
}

const formatAdvancedDateConstraint = (
    label: string,
    gte: string | undefined,
    lte: string | undefined
): string | null => {
    if (gte && lte) {
        if (gte === lte) return `${label} in ${formatDisplayDate(gte)}`
        return `${label} between ${formatDisplayDate(gte)} and ${formatDisplayDate(lte)}`
    }
    if (gte) return `${label} on or after ${formatDisplayDate(gte)}`
    if (lte) return `${label} on or before ${formatDisplayDate(lte)}`
    return null
}

const buildSentenceParts = (
    selectedLabels: string[] | undefined,
    timeFrame: string | undefined,
    rangeGte: string | undefined,
    rangeLte: string | undefined,
    companyPopoverContent: React.ReactNode,
    timeFramePopoverContent: React.ReactNode,
    advancedStartGte?: string,
    advancedStartLte?: string,
    advancedEndGte?: string,
    advancedEndLte?: string
): SentencePart[] => {
    const hasCompanies = selectedLabels && selectedLabels.length > 0
    const names = hasCompanies
        ? formatListWithOr(selectedLabels)
        : 'any company'
    const { spacer, label: timeLabel } = getTimeFrameLabel(
        timeFrame,
        rangeGte,
        rangeLte
    )

    const advancedParts: string[] = []
    const startPart = formatAdvancedDateConstraint(
        'started',
        advancedStartGte,
        advancedStartLte
    )
    const endPart = formatAdvancedDateConstraint(
        'ended',
        advancedEndGte,
        advancedEndLte
    )
    if (startPart) advancedParts.push(startPart)
    if (endPart) advancedParts.push(endPart)
    const advancedSuffix =
        advancedParts.length > 0 ? ` (${advancedParts.join(', ')})` : ''

    const timePart = hasCompanies
        ? {
              type: 'interactive' as const,
              label: timeLabel + advancedSuffix,
              popoverContent: timeFramePopoverContent,
          }
        : { type: 'text' as const, content: timeLabel + advancedSuffix }

    return [
        { type: 'text', content: 'Part of ' },
        {
            type: 'interactive',
            label: names,
            popoverContent: companyPopoverContent,
        },
        { type: 'text', content: ` ${spacer}` },
        timePart,
    ]
}

const getTimeFrameLabelWithoutStatus = (
    rangeGte: string | undefined,
    rangeLte: string | undefined
): { spacer: string; label: string } | null => {
    if (rangeGte && rangeLte) {
        if (rangeGte === rangeLte) {
            return {
                spacer: 'in ',
                label: formatDisplayDate(rangeGte),
            }
        }
        return {
            spacer: 'from ',
            label: `${formatDisplayDate(rangeGte)} to ${formatDisplayDate(rangeLte)}`,
        }
    }
    if (rangeGte)
        return {
            spacer: 'from ',
            label: `${formatDisplayDate(rangeGte)} onwards`,
        }
    if (rangeLte)
        return {
            spacer: 'until ',
            label: `${formatDisplayDate(rangeLte)}`,
        }
    return null
}

const buildReadOnlySentenceParts = (
    selectedLabels: string[] | undefined,
    timeFrame: string | undefined,
    rangeGte: string | undefined,
    rangeLte: string | undefined,
    advancedStartGte?: string,
    advancedStartLte?: string,
    advancedEndGte?: string,
    advancedEndLte?: string
): SentencePart[] => {
    const hasCompanies = selectedLabels && selectedLabels.length > 0
    const names = hasCompanies
        ? formatListWithOr(selectedLabels)
        : 'any company'

    const advancedParts: string[] = []
    const startPart = formatAdvancedDateConstraint(
        'started',
        advancedStartGte,
        advancedStartLte
    )
    const endPart = formatAdvancedDateConstraint(
        'ended',
        advancedEndGte,
        advancedEndLte
    )
    if (startPart) advancedParts.push(startPart)
    if (endPart) advancedParts.push(endPart)
    const advancedSuffix =
        advancedParts.length > 0 ? ` (${advancedParts.join(', ')})` : ''

    const timeInfo = getTimeFrameLabelWithoutStatus(rangeGte, rangeLte)

    const statusPrefix =
        timeFrame === 'current'
            ? 'Currently '
            : timeFrame === 'former'
              ? 'In the past '
              : ''

    const parts: SentencePart[] = []

    if (statusPrefix) {
        parts.push({ type: 'bold', content: statusPrefix.slice(0, -1) })
        parts.push({ type: 'text', content: ' part of ' })
    } else {
        parts.push({ type: 'text', content: 'Part of ' })
    }

    parts.push({ type: 'bold', content: names })

    if (timeInfo) {
        parts.push({ type: 'text', content: ` ${timeInfo.spacer}` })
        parts.push({ type: 'bold', content: timeInfo.label + advancedSuffix })
    } else if (advancedSuffix) {
        parts.push({ type: 'text', content: advancedSuffix })
    }

    return parts
}

// ─── Company Filter Summary (Read-Only) ─────────────────────────────────────

interface CompanyFilterSummaryProps {
    companyNames: string[]
    timeFrame: string | undefined
    rangeGte: string | undefined
    rangeLte: string | undefined
    advancedStartGte?: string
    advancedStartLte?: string
    advancedEndGte?: string
    advancedEndLte?: string
}

export const CompanyFilterSummary: FC<CompanyFilterSummaryProps> = ({
    companyNames,
    timeFrame,
    rangeGte,
    rangeLte,
    advancedStartGte,
    advancedStartLte,
    advancedEndGte,
    advancedEndLte,
}) => (
    <SettingsSentence
        parts={buildReadOnlySentenceParts(
            companyNames,
            timeFrame,
            rangeGte,
            rangeLte,
            advancedStartGte,
            advancedStartLte,
            advancedEndGte,
            advancedEndLte
        )}
    />
)

// ─── Company Filter (Sentence Mode) ─────────────────────────────────────────

interface CompanyFilterSentenceProps {
    companyIds: string[]
    companyNames: string[]
    timeFrame: string | undefined
    rangeGte: string | undefined
    rangeLte: string | undefined
    onChangeIds: (ids: string[]) => void
    onChangeTimeFrame: (tf: string | undefined) => void
    onChangeRange: (gte: string | undefined, lte: string | undefined) => void
    advancedStartGte?: string
    advancedStartLte?: string
    advancedEndGte?: string
    advancedEndLte?: string
    onChangeAdvancedStart?: (
        gte: string | undefined,
        lte: string | undefined
    ) => void
    onChangeAdvancedEnd?: (
        gte: string | undefined,
        lte: string | undefined
    ) => void
}

export const CompanyFilterSentence: FC<CompanyFilterSentenceProps> = ({
    companyIds,
    companyNames,
    timeFrame,
    rangeGte,
    rangeLte,
    onChangeIds,
    onChangeTimeFrame,
    onChangeRange,
    advancedStartGte,
    advancedStartLte,
    advancedEndGte,
    advancedEndLte,
    onChangeAdvancedStart,
    onChangeAdvancedEnd,
}) => (
    <SettingsSentence
        parts={buildSentenceParts(
            companyNames,
            timeFrame,
            rangeGte,
            rangeLte,
            <Box sx={{ width: 300 }}>
                <CompanyAutocomplete
                    value={companyIds}
                    onChange={onChangeIds}
                    disablePortal={false}
                />
            </Box>,
            <TimeFrameControls
                timeFrame={timeFrame}
                rangeGte={rangeGte}
                rangeLte={rangeLte}
                onChangeTimeFrame={onChangeTimeFrame}
                onChangeRange={onChangeRange}
                advancedStartGte={advancedStartGte}
                advancedStartLte={advancedStartLte}
                advancedEndGte={advancedEndGte}
                advancedEndLte={advancedEndLte}
                onChangeAdvancedStart={onChangeAdvancedStart}
                onChangeAdvancedEnd={onChangeAdvancedEnd}
            />,
            advancedStartGte,
            advancedStartLte,
            advancedEndGte,
            advancedEndLte
        )}
    />
)

// ─── Name Filter ────────────────────────────────────────────────────────────

export const NameFilter: FC<FilterProps<'name'>> = ({ value, onChange }) => (
    <Box
        sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
        }}
    >
        <TextField
            fullWidth
            size="small"
            placeholder="Search by name..."
            value={value || ''}
            onChange={(e) => onChange(e.target.value || undefined)}
            sx={minimalInputSx}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault()
                }
            }}
        />
    </Box>
)

// ─── Star Rating ────────────────────────────────────────────────────────────

export const StarRatingFilter: FC<{
    value: { min: number | undefined; max: number | undefined }
    onChange: (min: number | undefined, max: number | undefined) => void
}> = ({ value: { min }, onChange }) => {
    const stars = [1, 2, 3, 4, 5]
    const currentMin = min ?? 0

    const handleClick = (star: number) => {
        if (star === currentMin) {
            onChange(undefined, undefined)
        } else {
            onChange(star, 5)
        }
    }

    return (
        <FilterWrapper label="Average Star Rating">
            <Stack direction="row" alignItems="center" gap={0.5}>
                <Box
                    data-testid="star-rating-filter"
                    sx={{ display: 'flex', gap: 0.25 }}
                >
                    {stars.map((star) => {
                        const isActive = min !== undefined && star <= currentMin
                        return (
                            <Box
                                key={star}
                                onClick={() => handleClick(star)}
                                sx={{
                                    cursor: 'pointer',
                                    fontSize: '1.25rem',
                                    color: isActive
                                        ? 'warning.main'
                                        : 'grey.300',
                                    '&:hover': { color: 'warning.light' },
                                }}
                            >
                                ★
                            </Box>
                        )
                    })}
                </Box>
                {min !== undefined && min < 5 && (
                    <Typography
                        sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}
                    >
                        & Up
                    </Typography>
                )}
            </Stack>
        </FilterWrapper>
    )
}

// ─── Screening Type ─────────────────────────────────────────────────────────

export const ScreeningTypeFilter: FC<{
    templateIds: string[]
    starMin: number | undefined
    starMax: number | undefined
    onChange: (
        templateIds: string[],
        starMin: number | undefined,
        starMax: number | undefined
    ) => void
}> = ({ templateIds, starMin, starMax, onChange }) => {
    const { data } = useQuery(GetCandidateNoteTemplates)
    const screeningTemplates = (data?.noteTemplates ?? []).filter(
        (t) => t.isScreeningType
    )
    const stars = [1, 2, 3, 4, 5]
    const currentMin = starMin ?? 0

    const handleStarClick = (star: number) => {
        if (star === currentMin) {
            onChange(templateIds, undefined, undefined)
        } else {
            onChange(templateIds, star, 5)
        }
    }

    return (
        <FilterWrapper label="Screening Type">
            <Stack gap={1}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {screeningTemplates.map((t) => {
                        const isSelected = templateIds.includes(t.id)
                        return (
                            <Chip
                                key={t.id}
                                label={startCase(t.templateType)}
                                size="small"
                                onClick={() => {
                                    const newIds = isSelected
                                        ? templateIds.filter(
                                              (id) => id !== t.id
                                          )
                                        : [...templateIds, t.id]
                                    onChange(
                                        newIds,
                                        newIds.length === 0
                                            ? undefined
                                            : starMin,
                                        newIds.length === 0
                                            ? undefined
                                            : starMax
                                    )
                                }}
                                sx={toggleChipSx(isSelected)}
                            />
                        )
                    })}
                </Box>
                {templateIds.length > 0 && (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                            {stars.map((star) => {
                                const isActive =
                                    starMin !== undefined && star <= currentMin
                                return (
                                    <Box
                                        key={star}
                                        onClick={() => handleStarClick(star)}
                                        sx={{
                                            cursor: 'pointer',
                                            fontSize: '1.25rem',
                                            color: isActive
                                                ? 'warning.main'
                                                : 'grey.300',
                                            '&:hover': {
                                                color: 'warning.light',
                                            },
                                        }}
                                    >
                                        ★
                                    </Box>
                                )
                            })}
                        </Box>
                        {starMin !== undefined && starMin < 5 && (
                            <Typography
                                sx={{
                                    fontSize: '0.6875rem',
                                    color: 'text.secondary',
                                }}
                            >
                                & Up
                            </Typography>
                        )}
                        {starMin !== undefined && (
                            <Box
                                onClick={() =>
                                    onChange(templateIds, undefined, undefined)
                                }
                                sx={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    '&:hover': { color: 'error.main' },
                                }}
                            >
                                <Clear sx={{ fontSize: '0.75rem' }} />
                            </Box>
                        )}
                    </Stack>
                )}
            </Stack>
        </FilterWrapper>
    )
}

// ─── Experience Filter with Time Frame ──────────────────────────────────────

interface ExperienceFilterProps {
    label: string
    ids: string[]
    idsNot?: string[]
    timeFrame: string | undefined
    rangeGte?: string
    rangeLte?: string
    onChangeIds: (ids: string[], idsNot?: string[]) => void
    onChangeTimeFrame: (timeFrame: string | undefined) => void
    onChangeRange?: (gte: string | undefined, lte: string | undefined) => void
    filterComponent: FC<{
        value: string[]
        valueNot?: string[]
        onChange: (ids: string[], idsNot?: string[]) => void
    }>
    includeStateCountry?: boolean
    onChangeIncludeStateCountry?: (value: boolean) => void
    topTierOnly?: boolean
    onChangeTopTierOnly?: (value: boolean) => void
}

export const FilterWithTimeFrame: FC<ExperienceFilterProps> = ({
    label,
    ids,
    idsNot,
    timeFrame,
    rangeGte,
    rangeLte,
    onChangeIds,
    onChangeTimeFrame,
    onChangeRange,
    filterComponent: FilterComponent,
    includeStateCountry,
    onChangeIncludeStateCountry,
    topTierOnly,
    onChangeTopTierOnly,
}) => {
    return (
        <FilterWrapper
            label={label}
            headerActions={
                onChangeTopTierOnly || onChangeIncludeStateCountry ? (
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                        {onChangeIncludeStateCountry && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeStateCountry ?? false}
                                        onChange={(e) =>
                                            onChangeIncludeStateCountry(
                                                e.target.checked
                                            )
                                        }
                                        size="small"
                                        sx={filterCheckboxSx}
                                    />
                                }
                                label="State/country only"
                                sx={filterCheckboxLabelSx}
                            />
                        )}
                        {onChangeTopTierOnly && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={topTierOnly ?? false}
                                        onChange={(e) =>
                                            onChangeTopTierOnly(
                                                e.target.checked
                                            )
                                        }
                                        size="small"
                                        sx={filterCheckboxSx}
                                    />
                                }
                                label="Top Tier"
                                sx={filterCheckboxLabelSx}
                            />
                        )}
                    </Box>
                ) : undefined
            }
        >
            <Stack gap={1}>
                <FilterComponent
                    value={ids}
                    valueNot={idsNot}
                    onChange={onChangeIds}
                />
                {(ids.length > 0 || (idsNot?.length ?? 0) > 0) && (
                    <>
                        <RadioGroup
                            row
                            value={timeFrame || 'any'}
                            onChange={(e) => {
                                const val = e.target.value
                                onChangeTimeFrame(
                                    val === 'any' ? undefined : val
                                )
                            }}
                            sx={{ gap: 0, justifyContent: 'center' }}
                        >
                            {['any', 'current', 'past', 'range'].map((tf) => (
                                <FormControlLabel
                                    key={tf}
                                    value={tf}
                                    control={
                                        <Radio size="small" sx={{ p: 0.5 }} />
                                    }
                                    label={
                                        tf.charAt(0).toUpperCase() + tf.slice(1)
                                    }
                                    sx={{
                                        mr: 1.5,
                                        ...smallCheckboxLabelSx,
                                    }}
                                />
                            ))}
                        </RadioGroup>
                        {timeFrame === 'range' && (
                            <DateRangeInput
                                from={
                                    rangeGte ? parseYearMonth(rangeGte) : null
                                }
                                to={rangeLte ? parseYearMonth(rangeLte) : null}
                                onFromChange={(date) =>
                                    onChangeRange?.(
                                        date
                                            ? formatYearMonth(date)
                                            : undefined,
                                        rangeLte
                                    )
                                }
                                onToChange={(date) =>
                                    onChangeRange?.(
                                        rangeGte,
                                        date ? formatYearMonth(date) : undefined
                                    )
                                }
                                fromLabel="Start"
                                toLabel="End"
                                maxDate={new Date()}
                            />
                        )}
                    </>
                )}
            </Stack>
        </FilterWrapper>
    )
}

// ─── Autocomplete Components (for FilterWithTimeFrame) ──────────────────────

export const CompanyAutocomplete: FC<{
    value: string[]
    onChange: (ids: string[]) => void
    disablePortal?: boolean
}> = ({ value, onChange, disablePortal }) => {
    const { data, previousData } = useQuery(GetCompaniesById, {
        variables: { ids: value },
        skip: value.length === 0,
    })
    const companiesById = (data ?? previousData)?.companiesById ?? []
    const selectedCompanies = value.map((id) => {
        const found = companiesById.find((c) => c.id === id)
        return { id, value: found?.value ?? '' }
    })

    return (
        <CompaniesAutocompleteMultiple
            size="small"
            label=""
            placeholder="Type to search..."
            value={selectedCompanies}
            onChange={(_, newVal) => onChange(newVal.map((c) => c.id))}
            sx={minimalInputSx}
            renderTags={renderMinimalTags(
                (opt: { id: string; value: string }) => opt.value || '…'
            )}
            ListboxProps={compactListboxProps}
            disablePortal={disablePortal}
        />
    )
}

// ─── GPA Filter ─────────────────────────────────────────────────────────────

const GPA_SLIDER_MIN = 0
const GPA_SLIDER_MAX = 4
const GPA_SLIDER_STEP = 0.1

const gpaSliderMarks = [
    { value: 0, label: '0' },
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4+' },
]

export type GpaNoGpaMode = 'include' | 'exclude' | 'only'

export const GpaFilter: FC<{
    min: number | undefined
    max: number | undefined
    noGpaMode: GpaNoGpaMode | undefined
    onChange: (min: number | undefined, max: number | undefined) => void
    onChangeNoGpaMode: (mode: GpaNoGpaMode | undefined) => void
    onClear?: () => void
}> = ({ min, max, noGpaMode, onChange, onChangeNoGpaMode, onClear }) => {
    const effectiveMode = noGpaMode ?? 'include'
    const isOnlyMode = effectiveMode === 'only'

    const isDefaultRange = min === undefined && max === undefined
    const isDefaultMode = noGpaMode === undefined
    const hasActiveFilter = !isDefaultRange || !isDefaultMode

    const sliderValue: [number, number] = [
        min ?? GPA_SLIDER_MIN,
        max ?? GPA_SLIDER_MAX,
    ]

    const handleSliderChange = (_event: Event, newValue: number | number[]) => {
        if (!Array.isArray(newValue)) return
        const [newMin, newMax] = newValue
        const isDefault = newMin === GPA_SLIDER_MIN && newMax === GPA_SLIDER_MAX
        onChange(isDefault ? undefined : newMin, isDefault ? undefined : newMax)
    }

    const handleModeChange = (newMode: GpaNoGpaMode) => {
        const isDefaultMode = newMode === 'include'
        onChangeNoGpaMode(isDefaultMode ? undefined : newMode)
    }

    const handleClear = () => {
        if (onClear) {
            onClear()
        } else {
            onChange(undefined, undefined)
            onChangeNoGpaMode(undefined)
        }
    }

    return (
        <FilterWrapper
            label="GPA"
            headerActions={
                hasActiveFilter ? (
                    <Typography
                        component="span"
                        onClick={(e) => {
                            e.stopPropagation()
                            handleClear()
                        }}
                        sx={{
                            fontSize: '0.625rem',
                            color: 'primary.main',
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        Clear
                    </Typography>
                ) : undefined
            }
        >
            <Stack gap={0}>
                <Box sx={{ px: 1 }}>
                    <Slider
                        value={sliderValue}
                        onChange={handleSliderChange}
                        min={GPA_SLIDER_MIN}
                        max={GPA_SLIDER_MAX}
                        step={GPA_SLIDER_STEP}
                        marks={gpaSliderMarks}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) =>
                            v === GPA_SLIDER_MAX ? '4+' : v.toFixed(1)
                        }
                        size="small"
                        disabled={isOnlyMode}
                        sx={{
                            '& .MuiSlider-markLabel': {
                                fontSize: '0.625rem',
                                userSelect: 'none',
                            },
                        }}
                    />
                </Box>
                <RadioGroup
                    value={effectiveMode}
                    onChange={(e) =>
                        handleModeChange(e.target.value as GpaNoGpaMode)
                    }
                    sx={{ gap: 0, px: 1 }}
                >
                    <FormControlLabel
                        value="include"
                        control={<Radio size="small" sx={{ p: 0.5 }} />}
                        label="Include candidates with no GPA"
                        sx={smallCheckboxLabelSx}
                    />
                    <FormControlLabel
                        value="exclude"
                        control={<Radio size="small" sx={{ p: 0.5 }} />}
                        label="Exclude candidates with no GPA"
                        sx={smallCheckboxLabelSx}
                    />
                    <FormControlLabel
                        value="only"
                        control={<Radio size="small" sx={{ p: 0.5 }} />}
                        label="Only candidates with no GPA"
                        sx={smallCheckboxLabelSx}
                    />
                </RadioGroup>
            </Stack>
        </FilterWrapper>
    )
}

// ─── Date Range Filters ─────────────────────────────────────────────────────

export const AnalystClassYearFilter: FC<{
    gte: number | undefined
    lte: number | undefined
    onChange: (gte: number | undefined, lte: number | undefined) => void
}> = ({ gte, lte, onChange }) => (
    <FilterWrapper label="Analyst Class Year">
        <DateRangeInput
            from={gte ? new Date(gte, 0, 1) : null}
            to={lte ? new Date(lte, 0, 1) : null}
            onFromChange={(date) =>
                onChange(date ? date.getFullYear() : undefined, lte)
            }
            onToChange={(date) =>
                onChange(gte, date ? date.getFullYear() : undefined)
            }
            format="yyyy"
            views={['year']}
        />
    </FilterWrapper>
)

export const GraduationDateFilter: FC<{
    gte: number | undefined
    lte: number | undefined
    educationType: string | undefined
    onChange: (
        gte: number | undefined,
        lte: number | undefined,
        educationType: string | undefined
    ) => void
}> = ({ gte, lte, educationType, onChange }) => (
    <FilterWrapper label="Graduation Year">
        <Stack gap={1}>
            <DateRangeInput
                from={gte ? new Date(gte, 0) : null}
                to={lte ? new Date(lte, 0) : null}
                onFromChange={(date) => {
                    onChange(date?.getFullYear(), lte, educationType)
                }}
                onToChange={(date) => {
                    onChange(gte, date?.getFullYear(), educationType)
                }}
                format="yyyy"
                views={['year']}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {EDUCATION_TYPE_OPTIONS.map((opt) => (
                    <Chip
                        key={opt}
                        label={opt}
                        size="small"
                        onClick={() =>
                            onChange(
                                gte,
                                lte,
                                educationType === opt ? undefined : opt
                            )
                        }
                        sx={toggleChipSx(educationType === opt)}
                    />
                ))}
            </Box>
        </Stack>
    </FilterWrapper>
)

export const StartDateFilter: FC<{
    gte: string | undefined
    lte: string | undefined
    onChange: (gte: string | undefined, lte: string | undefined) => void
}> = ({ gte, lte, onChange }) => (
    <FilterWrapper label="Start Date">
        <DateRangeInput
            from={gte ? parseYearMonth(gte) : null}
            to={lte ? parseYearMonth(lte) : null}
            onFromChange={(date) =>
                onChange(date ? formatYearMonth(date) : undefined, lte)
            }
            onToChange={(date) =>
                onChange(gte, date ? formatYearMonth(date) : undefined)
            }
        />
    </FilterWrapper>
)

// ─── Searches ───────────────────────────────────────────────────────────────

export const SearchesFilterBuilder: FC<{
    searchFilters: SearchFilterItem[]
    onChange: (filters: SearchFilterItem[]) => void
}> = ({ searchFilters, onChange }) => {
    const [showDrafts, setShowDrafts] = useState(false)
    const [onlyMyRosters, setOnlyMyRosters] = useState(false)
    const [selectedRoster, setSelectedRoster] = useState<{
        id: string
        rosterName: string
    } | null>(null)
    const [selectedProcessStatus, setSelectedProcessStatus] = useState<{
        id: string
        type: 'status' | 'group'
    } | null>(null)
    const [resetKey, setResetKey] = useState(0)
    const [prefilledRoster, setPrefilledRoster] = useState<
        NonNullable<GetRostersByIdsQuery['rostersByIds']>[number] | null
    >(null)

    const { refColumnRosterIds } = useReferenceSearchesUrlState()
    const filterRosterIds = new Set(
        searchFilters.map((f) => f.rosterId).filter(Boolean)
    )
    const { data: refRostersData } = useQuery<GetRostersByIdsQuery>(
        GetRostersByIds,
        {
            variables: { ids: refColumnRosterIds },
            skip: refColumnRosterIds.length === 0,
        }
    )
    const suggestedRosters = (refRostersData?.rostersByIds ?? []).filter(
        (r) => !filterRosterIds.has(r.id)
    )

    const rosterIds = searchFilters
        .map((f) => f.rosterId)
        .filter(Boolean) as string[]
    const { data: rosterValues, previousData: prevRosterValues } =
        useQuery<GetRostersByIdsQuery>(GetRostersByIds, {
            variables: { ids: rosterIds },
            skip: rosterIds.length === 0,
        })
    const rosterMap = new Map(
        ((rosterValues ?? prevRosterValues)?.rostersByIds ?? []).map((r) => [
            r.id,
            r.rosterName,
        ])
    )

    const {
        data: processStatusData,
        previousData: prevProcessStatusData,
        loading: processStatusLoading,
    } = useQuery<GetProcessStatusesQuery>(GetProcessStatuses)

    const OUT_OF_PROCESS_SENTINEL = 'OUT_OF_PROCESS'
    const statusData = processStatusData ?? prevProcessStatusData
    const rawStatuses = statusData?.processStatuses ?? []
    const outOfProcessGroup = rawStatuses.find(
        (ps) =>
            ps.processStatusSummaryGroup?.summaryGroup ===
            ProcessStatusGroup.OutOfProcess
    )?.processStatusSummaryGroup
    const outOfProcessGroupLabel = outOfProcessGroup?.label ?? 'Out of Process'

    type ProcessStatusOption = {
        id: string
        label: string
        type: 'group' | 'status' | 'divider'
        groupLabel: string
        groupId: string
    }

    const allProcessStatusOptions: ProcessStatusOption[] = rawStatuses
        .slice()
        .sort(
            (a, b) =>
                (a.processStatusSummaryGroup?.label ?? '').localeCompare(
                    b.processStatusSummaryGroup?.label ?? ''
                ) || a.statusLabel.localeCompare(b.statusLabel)
        )
        .map((ps) => ({
            id: ps.id,
            label: ps.statusLabel,
            type: 'status' as const,
            groupLabel: ps.processStatusSummaryGroup?.label ?? 'No Group',
            groupId: ps.processStatusSummaryGroup?.id ?? '',
        }))
        .reduce<ProcessStatusOption[]>((acc, opt) => {
            const lastGroup = acc.findLast((o) => o.type === 'group')
            if (lastGroup?.groupLabel !== opt.groupLabel) {
                acc.push({
                    id: opt.groupId,
                    label: opt.groupLabel,
                    type: 'group',
                    groupLabel: opt.groupLabel,
                    groupId: opt.groupId,
                })
                if (opt.groupLabel === outOfProcessGroupLabel) {
                    acc.push({
                        id: OUT_OF_PROCESS_SENTINEL,
                        label: 'Out of Process',
                        type: 'status',
                        groupLabel: outOfProcessGroupLabel,
                        groupId: opt.groupId,
                    })
                }
            }
            acc.push(opt)
            return acc
        }, [])

    const psGroupShortcuts = allProcessStatusOptions.filter(
        (o) => o.type === 'group'
    )
    const psDivider: ProcessStatusOption = {
        id: '__divider__',
        label: '',
        type: 'divider',
        groupLabel: '',
        groupId: '',
    }
    const psDisplayOptions: ProcessStatusOption[] = [
        ...psGroupShortcuts,
        psDivider,
        ...allProcessStatusOptions,
    ]

    const processStatusMap = new Map(
        allProcessStatusOptions
            .filter((o) => o.type === 'status')
            .map((ps) => [ps.id, ps.label])
    )
    const processStatusGroupMap = new Map(
        psGroupShortcuts.map((g) => [g.id, g.label])
    )

    const handleAdd = () => {
        if (!selectedRoster && !selectedProcessStatus) return
        const statusPart: Partial<SearchFilterItem> =
            selectedProcessStatus?.type === 'group'
                ? { processStatusGroupId: selectedProcessStatus.id }
                : selectedProcessStatus
                  ? { processStatusId: selectedProcessStatus.id }
                  : {}
        const newItem: SearchFilterItem = selectedRoster
            ? { rosterId: selectedRoster.id, ...statusPart }
            : { ...statusPart }
        onChange([...searchFilters, newItem])
        setSelectedRoster(null)
        setSelectedProcessStatus(null)
        setPrefilledRoster(null)
        setResetKey((k) => k + 1)
    }

    const handleRemove = (index: number) => {
        onChange(searchFilters.filter((_, i) => i !== index))
    }

    const getChipLabel = (item: SearchFilterItem) => {
        const statusName = item.processStatusId
            ? processStatusMap.get(item.processStatusId)
            : item.processStatusGroupId
              ? processStatusGroupMap.get(item.processStatusGroupId)
              : null
        if (item.rosterId && statusName) {
            const rosterName = rosterMap.get(item.rosterId) ?? 'Loading...'
            return `${rosterName} · ${statusName}`
        }
        if (item.rosterId) {
            return rosterMap.get(item.rosterId) ?? 'Loading...'
        }
        if (statusName) {
            return `Any Search · ${statusName}`
        }
        return 'Unknown'
    }

    return (
        <Stack gap={1.5}>
            <FilterWrapper
                label="Add Search Filter"
                headerActions={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={onlyMyRosters}
                                    onChange={(e) =>
                                        setOnlyMyRosters(e.target.checked)
                                    }
                                    size="small"
                                    sx={filterCheckboxSx}
                                />
                            }
                            label="My Searches"
                            sx={filterCheckboxLabelSx}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showDrafts}
                                    onChange={(e) =>
                                        setShowDrafts(e.target.checked)
                                    }
                                    size="small"
                                    sx={filterCheckboxSx}
                                />
                            }
                            label="Show Drafts"
                            sx={filterCheckboxLabelSx}
                        />
                    </Box>
                }
            >
                <Stack gap={1}>
                    <RostersAutocomplete
                        key={`${resetKey}-${showDrafts}-${onlyMyRosters}`}
                        size="small"
                        label=""
                        placeholder="Select a search..."
                        initialValue={prefilledRoster}
                        onSingleSelectionChange={(roster) =>
                            setSelectedRoster(roster)
                        }
                        onlyMyRosters={onlyMyRosters}
                        includeDrafts={showDrafts}
                        includeClosed
                        textFieldSx={minimalInputSx}
                        ListboxProps={{
                            sx: {
                                '& .MuiAutocomplete-option': {
                                    p: 0,
                                    minHeight: 'unset',
                                },
                                '& .MuiListItemButton-root': {
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    px: 1,
                                    minHeight: 'unset',
                                },
                            },
                        }}
                    />
                    <Autocomplete<ProcessStatusOption>
                        size="small"
                        options={psDisplayOptions}
                        loading={processStatusLoading}
                        getOptionLabel={(o) => o.label}
                        getOptionDisabled={(o) => o.type === 'divider'}
                        isOptionEqualToValue={(a, b) =>
                            a.id === b.id && a.type === b.type
                        }
                        value={
                            selectedProcessStatus
                                ? allProcessStatusOptions.find(
                                      (o) =>
                                          o.id === selectedProcessStatus.id &&
                                          (selectedProcessStatus.type ===
                                          'group'
                                              ? o.type === 'group'
                                              : o.type === 'status')
                                  ) ?? null
                                : null
                        }
                        onChange={(_, newVal) =>
                            setSelectedProcessStatus(
                                newVal
                                    ? {
                                          id: newVal.id,
                                          type:
                                              newVal.type === 'group'
                                                  ? 'group'
                                                  : 'status',
                                      }
                                    : null
                            )
                        }
                        filterOptions={(options, state) => {
                            const inputValue = state.inputValue.toLowerCase()
                            if (!inputValue) return options
                            return options.filter(
                                (o) =>
                                    o.type === 'divider' ||
                                    o.label.toLowerCase().includes(inputValue)
                            )
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Process status"
                                sx={minimalInputSx}
                            />
                        )}
                        renderOption={(
                            { key, ...optionProps },
                            option,
                            { index }
                        ) => {
                            if (option.type === 'divider') {
                                return (
                                    <Divider
                                        key="__divider__"
                                        sx={{ my: 0.5 }}
                                    />
                                )
                            }
                            const isGroup = option.type === 'group'
                            const isShortcut =
                                isGroup && index < psGroupShortcuts.length
                            const isSelected =
                                selectedProcessStatus?.id === option.id &&
                                (selectedProcessStatus?.type === 'group') ===
                                    isGroup
                            return (
                                <Box
                                    component="li"
                                    key={`${isShortcut ? 'shortcut' : option.type}-${option.id}`}
                                    {...optionProps}
                                    sx={{
                                        fontSize: isGroup
                                            ? '0.6875rem'
                                            : '0.75rem',
                                        fontWeight: isGroup ? 600 : 400,
                                        py: isGroup ? 0.25 : 0.5,
                                        pl: isShortcut ? 1 : isGroup ? 1 : 3,
                                        letterSpacing: isGroup
                                            ? '0.05em'
                                            : 'normal',
                                        bgcolor: isSelected
                                            ? 'primary.50'
                                            : isGroup
                                              ? 'grey.50'
                                              : 'inherit',
                                        borderLeft: isSelected
                                            ? '3px solid'
                                            : 'none',
                                        borderLeftColor: 'primary.main',
                                        minHeight: 'unset',
                                    }}
                                >
                                    {option.label}
                                </Box>
                            )
                        }}
                        ListboxProps={compactListboxProps}
                    />
                    <Box
                        component="button"
                        onClick={handleAdd}
                        disabled={!selectedRoster && !selectedProcessStatus}
                        sx={{
                            alignSelf: 'flex-start',
                            px: 1.5,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor:
                                selectedRoster || selectedProcessStatus
                                    ? 'primary.main'
                                    : 'action.disabledBackground',
                            color:
                                selectedRoster || selectedProcessStatus
                                    ? 'primary.contrastText'
                                    : 'text.disabled',
                            cursor:
                                selectedRoster || selectedProcessStatus
                                    ? 'pointer'
                                    : 'not-allowed',
                            '&:hover':
                                selectedRoster || selectedProcessStatus
                                    ? { bgcolor: 'primary.dark' }
                                    : {},
                        }}
                    >
                        Add
                    </Box>
                </Stack>
            </FilterWrapper>
            {suggestedRosters.length > 0 && (
                <FilterWrapper label="Suggested from Reference Columns">
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {suggestedRosters.map((roster) => (
                            <Chip
                                key={roster.id}
                                label={roster.rosterName}
                                icon={<Add sx={{ fontSize: '0.875rem' }} />}
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    setSelectedRoster({
                                        id: roster.id,
                                        rosterName: roster.rosterName,
                                    })
                                    setPrefilledRoster(roster)
                                    setResetKey((k) => k + 1)
                                }}
                                sx={{
                                    fontSize: '0.6875rem',
                                    height: 24,
                                    borderStyle: 'dashed',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'primary.50' },
                                }}
                            />
                        ))}
                    </Box>
                </FilterWrapper>
            )}
            {searchFilters.length > 0 && (
                <FilterWrapper label="Active Filters">
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {searchFilters.map((item, index) => {
                            const label = getChipLabel(item)
                            return (
                                <Chip
                                    key={index}
                                    label={label}
                                    title={label}
                                    onClick={() => handleRemove(index)}
                                    size="small"
                                    sx={{
                                        ...minimalTagSx(false),
                                        cursor: 'pointer',
                                    }}
                                />
                            )
                        })}
                    </Box>
                </FilterWrapper>
            )}
        </Stack>
    )
}

// ─── Preference Filter ──────────────────────────────────────────────────────

interface PreferenceFilterProps {
    label: string
    first: string[]
    second: string[]
    third: string[]
    onChange: (first: string[], second: string[], third: string[]) => void
    options: Array<{ id: string; value: string }>
    loading: boolean
}

export const PreferenceFilter: FC<PreferenceFilterProps> = ({
    label,
    first,
    second,
    third,
    onChange,
    options,
    loading,
}) => {
    const renderPrefAutocomplete = (
        prefLabel: string,
        value: string[],
        onPrefChange: (ids: string[]) => void
    ) => {
        const NO_PREFERENCE_OPTION = {
            id: 'NO_PREFERENCE',
            value: 'No Preference',
        }
        const allOptions = [NO_PREFERENCE_OPTION, ...options]
        const selected = allOptions.filter((o) => value.includes(o.id))
        return (
            <Box>
                <Typography sx={{ ...filterLabelSx, fontSize: '0.5625rem' }}>
                    {prefLabel}
                </Typography>
                <FilterAutocomplete
                    loading={loading}
                    options={allOptions}
                    value={selected}
                    getOptionLabel={(o) => o.value}
                    getTagLabel={(o) => o.value}
                    getOptionKey={(o) => o.id}
                    onChange={(_, newVal) =>
                        onPrefChange(newVal.map((o) => o.id))
                    }
                />
            </Box>
        )
    }

    return (
        <FilterWrapper label={label}>
            <Stack gap={1}>
                {renderPrefAutocomplete('1st Choice', first, (v) =>
                    onChange(v, second, third)
                )}
                {renderPrefAutocomplete('2nd Choice', second, (v) =>
                    onChange(first, v, third)
                )}
                {renderPrefAutocomplete('3rd Choice', third, (v) =>
                    onChange(first, second, v)
                )}
            </Stack>
        </FilterWrapper>
    )
}

interface TwoTierPreferenceFilterProps {
    label: string
    first: string[]
    second: string[]
    onChange: (first: string[], second: string[]) => void
    options: Array<{ id: string; value: string }>
    loading: boolean
}

export const TwoTierPreferenceFilter: FC<TwoTierPreferenceFilterProps> = ({
    label,
    first,
    second,
    onChange,
    options,
    loading,
}) => {
    const renderPrefAutocomplete = (
        prefLabel: string,
        value: string[],
        onPrefChange: (ids: string[]) => void
    ) => {
        const NO_PREFERENCE_OPTION = {
            id: 'NO_PREFERENCE',
            value: 'No Preference',
        }
        const allOptions = [NO_PREFERENCE_OPTION, ...options]
        const selected = allOptions.filter((o) => value.includes(o.id))
        return (
            <Box>
                <Typography sx={{ ...filterLabelSx, fontSize: '0.5625rem' }}>
                    {prefLabel}
                </Typography>
                <FilterAutocomplete
                    loading={loading}
                    options={allOptions}
                    value={selected}
                    getOptionLabel={(o) => o.value}
                    getTagLabel={(o) => o.value}
                    getOptionKey={(o) => o.id}
                    onChange={(_, newVal) =>
                        onPrefChange(newVal.map((o) => o.id))
                    }
                />
            </Box>
        )
    }

    return (
        <FilterWrapper label={label}>
            <Stack gap={1}>
                {renderPrefAutocomplete('1st Choice', first, (v) =>
                    onChange(v, second)
                )}
                {renderPrefAutocomplete('2nd Choice', second, (v) =>
                    onChange(first, v)
                )}
            </Stack>
        </FilterWrapper>
    )
}

// ─── Hometown Filter ────────────────────────────────────────────────────────

export const HometownFilter: FC<FilterProps<'hometownSearchTerms'>> = ({
    value,
    onChange,
}) => {
    const handleAdd = (term: string) => {
        if (term && !value.includes(term)) {
            onChange([...value, term])
        }
    }

    return (
        <FilterWrapper label="Hometown">
            <Stack gap={0.5}>
                <TextField
                    size="small"
                    placeholder="Type and press Enter..."
                    sx={minimalInputSx}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            const input = e.target as HTMLInputElement
                            handleAdd(input.value.trim())
                            input.value = ''
                        }
                    }}
                />
                {value.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {value.map((term) => (
                            <Typography
                                key={term}
                                component="span"
                                onClick={() =>
                                    onChange(value.filter((t) => t !== term))
                                }
                                sx={minimalTagSx(false)}
                            >
                                {term}
                                <Clear sx={{ fontSize: '0.625rem' }} />
                            </Typography>
                        ))}
                    </Box>
                )}
            </Stack>
        </FilterWrapper>
    )
}
